// frontend/src/contexts/CartContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { BookingKind } from '../services/api';

export type CartBookingMode = 'slot' | 'walk_in' | 'phone';

/** Единственное место, где режим врача превращается в режим позиции корзины. */
export const bookingModeFor = (kind: BookingKind): CartBookingMode =>
  kind === 'slots' ? 'slot' : kind === 'walkin' ? 'walk_in' : 'phone';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  slug: string;
  image?: string;
  department_name?: string;
  // Приём по видеосвязи (Service.is_online)
  is_online?: boolean;
  // Appointment slot (optional — set when booked from doctor page)
  appointment_date?: string;    // 'YYYY-MM-DD'
  appointment_time?: string;    // 'HH:MM'
  appointment_duration?: number; // minutes
  doctor_slug?: string;
  doctor_name?: string;
  doctor_id?: number;
  department_id?: number;
  // If set, this item is a companion service linked to the parent
  parent_id?: number;
  // Booking mode; undefined — врач ещё не выбран
  booking_mode?: CartBookingMode;
  time_preference?: 'morning' | 'afternoon';
  // Walk-in: doctor's default schedule for display purposes
  doctor_schedule?: Array<{ day_of_week: number; start_time: string; end_time: string }>;
}

/**
 * Уходит ли позиция в заявку. Очередь и телефон — справка и не уходят
 * никогда; запись на время — только с врачом, датой и временем.
 */
export const isSubmittable = (item: CartItem): boolean =>
  !item.parent_id
  && item.booking_mode === 'slot'
  && !!item.doctor_id
  && !!item.appointment_date
  && !!item.appointment_time;

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  setItemAppointment: (id: number, date: string, time: string, doctorSlug: string) => void;
  clearItemTime: (id: number) => void;
  updateItemDoctor: (id: number, doctor: { doctor_id: number; doctor_slug: string; doctor_name: string; image?: string; booking_mode?: CartBookingMode; doctor_schedule?: Array<{ day_of_week: number; start_time: string; end_time: string }>; nearest_walkin_date?: string }) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  isInCart: (id: number) => boolean;
  getItemAppointment: (id: number) => { date?: string; time?: string } | null;
  isSlotTaken: (doctorSlug: string, date: string, time: string) => boolean;
  // Check if a time interval [startMin, startMin+duration) conflicts with ANY cart item on that date (across all doctors)
  isTimeConflict: (date: string, startMin: number, durationMin: number, excludeId?: number) => boolean;
  getOccupiedEndTime: (doctorSlug: string, date: string) => number | null;
  // Returns all booked intervals [startMin, endMin] for a doctor/date, optionally excluding one service id
  getOccupiedIntervals: (doctorSlug: string, date: string, excludeId?: number) => Array<{ start: number; end: number }>;
  // Effective duration of an item = its own duration + all companion durations
  getEffectiveDuration: (id: number) => number;
  // Children (companion items) of a parent service in cart
  getChildren: (parentId: number) => CartItem[];
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'elitmed_cart';

/**
 * Корзина из localStorage — сразу в начальном состоянии.
 *
 * Читать её в useEffect нельзя: эффект записи выполняется в том же цикле
 * монтирования и успевает сохранить ещё пустой items, затирая сохранённое.
 * Корзина терялась при каждой перезагрузке страницы.
 */
const DOCTOR_RESET: Partial<CartItem> = {
  doctor_id: undefined, doctor_slug: undefined, doctor_name: undefined, image: undefined,
  booking_mode: undefined, appointment_date: undefined, appointment_time: undefined,
  time_preference: undefined, doctor_schedule: undefined,
};

/**
 * Корзины, сохранённые до единого режима записи.
 * Сетка слотов раньше клала позицию БЕЗ booking_mode — такие позиции с
 * врачом и временем и есть запись на время, сбрасывать их нельзя.
 * «Любой врач» был фиктивным врачом с id 0 — его выбирают заново.
 */
const migrateItem = (item: CartItem): CartItem => {
  if (item.doctor_id === 0) return { ...item, ...DOCTOR_RESET };
  if (item.booking_mode) return item;
  if (item.doctor_id && item.appointment_time) return { ...item, booking_mode: 'slot' };
  if (item.doctor_id) return { ...item, ...DOCTOR_RESET };
  return item;
};

const loadCart = (): CartItem[] => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    return saved ? (JSON.parse(saved) as CartItem[]).map(migrateItem) : [];
  } catch (error) {
    console.error('Failed to load cart from localStorage:', error);
    return [];
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (newItem: Omit<CartItem, 'quantity'>) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === newItem.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === newItem.id
            ? {
                ...item,
                appointment_date: newItem.appointment_date ?? item.appointment_date,
                appointment_time: newItem.appointment_time ?? item.appointment_time,
                appointment_duration: newItem.appointment_duration ?? item.appointment_duration,
                doctor_slug: newItem.doctor_slug ?? item.doctor_slug,
                doctor_name: newItem.doctor_name ?? item.doctor_name,
                doctor_id: newItem.doctor_id ?? item.doctor_id,
                image: newItem.image ?? item.image,
                doctor_schedule: newItem.doctor_schedule ?? item.doctor_schedule,
                parent_id: newItem.parent_id ?? item.parent_id,
                booking_mode: newItem.appointment_time !== undefined
                  ? newItem.booking_mode
                  : (newItem.booking_mode ?? item.booking_mode),
                time_preference: newItem.appointment_time !== undefined
                  ? newItem.time_preference
                  : (newItem.time_preference ?? item.time_preference),
              }
            : item
        );
      }
      return [...prevItems, { ...newItem, quantity: 1 }];
    });
  };

  const removeItem = (id: number) => {
    // Also remove all companion services whose parent is this item
    setItems((prevItems) => prevItems.filter((item) => item.id !== id && item.parent_id !== id));
  };

  const updateQuantity = (id: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const setItemAppointment = (id: number, date: string, time: string, doctorSlug: string) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? { ...item, appointment_date: date, appointment_time: time, doctor_slug: doctorSlug }
          : item
      )
    );
  };

  // Время позиции отклонил сервер (занято или недоступно) — пациент выбирает снова.
  const clearItemTime = (id: number) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id || item.parent_id === id
          ? { ...item, appointment_date: undefined, appointment_time: undefined }
          : item
      )
    );
  };

  const updateItemDoctor = (id: number, doctor: { doctor_id: number; doctor_slug: string; doctor_name: string; image?: string; booking_mode?: CartBookingMode; doctor_schedule?: Array<{ day_of_week: number; start_time: string; end_time: string }>; nearest_walkin_date?: string }) => {
    setItems((prevItems) => {
      return prevItems.map((item) =>
        item.id === id
          ? { ...item, ...doctor, appointment_date: doctor.nearest_walkin_date ?? undefined, appointment_time: undefined, time_preference: undefined }
          : item.parent_id === id
          ? { ...item, ...doctor }
          : item
      );
    });
  };

  const clearCart = () => setItems([]);

  const isInCart = (id: number) => items.some((item) => item.id === id);

  const getChildren = (parentId: number): CartItem[] =>
    items.filter((item) => item.parent_id === parentId);

  // Effective duration = own duration + sum of all companion durations
  const getEffectiveDuration = (id: number): number => {
    const item = items.find((i) => i.id === id);
    if (!item) return 0;
    const own = item.appointment_duration ?? 0;
    const companions = items
      .filter((i) => i.parent_id === id)
      .reduce((sum, i) => sum + (i.appointment_duration ?? 0), 0);
    return own + companions;
  };

  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // A slot at `time` is taken if any cart item for this doctor/date
  // occupies an interval that overlaps [slotStart, slotStart+1min).
  // For parent items, effective duration includes companions.
  // Companion items themselves don't independently block slots (their time = parent's time).
  const isSlotTaken = (doctorSlug: string, date: string, time: string) => {
    const slotStart = toMin(time);
    return items.some((item) => {
      if (item.parent_id) return false; // companions don't independently block
      if (item.doctor_slug !== doctorSlug || item.appointment_date !== date || !item.appointment_time) return false;
      const bookedStart = toMin(item.appointment_time);
      const effectiveDuration = getEffectiveDuration(item.id);
      const bookedEnd = bookedStart + effectiveDuration;
      return slotStart >= bookedStart && slotStart < bookedEnd;
    });
  };

  // Returns true if [startMin, startMin+durationMin) overlaps any cart item on that date (any doctor)
  const isTimeConflict = (date: string, startMin: number, durationMin: number, excludeId?: number): boolean => {
    const end = startMin + (durationMin || 1);
    return items.some((item) => {
      if (item.parent_id) return false;
      if (item.id === excludeId) return false;
      if (item.appointment_date !== date || !item.appointment_time) return false;
      const bookedStart = toMin(item.appointment_time);
      const bookedEnd = bookedStart + getEffectiveDuration(item.id);
      // Overlap: not (end <= bookedStart || startMin >= bookedEnd)
      return end > bookedStart && startMin < bookedEnd;
    });
  };

  // Returns the latest end minute across all booked parent services for this doctor/date.
  const getOccupiedEndTime = (doctorSlug: string, date: string): number | null => {
    let maxEnd: number | null = null;
    for (const item of items) {
      if (item.parent_id) continue; // skip companions
      if (item.doctor_slug !== doctorSlug || item.appointment_date !== date || !item.appointment_time) continue;
      const end = toMin(item.appointment_time) + getEffectiveDuration(item.id);
      if (maxEnd === null || end > maxEnd) maxEnd = end;
    }
    return maxEnd;
  };

  const getOccupiedIntervals = (doctorSlug: string, date: string, excludeId?: number) => {
    const result: Array<{ start: number; end: number }> = [];
    for (const item of items) {
      if (item.parent_id) continue;
      if (item.id === excludeId) continue;
      if (item.doctor_slug !== doctorSlug || item.appointment_date !== date || !item.appointment_time) continue;
      const start = toMin(item.appointment_time);
      const end = start + getEffectiveDuration(item.id);
      result.push({ start, end });
    }
    return result;
  };

  const getItemAppointment = (id: number) => {
    const item = items.find((i) => i.id === id);
    if (!item) return null;
    return { date: item.appointment_date, time: item.appointment_time };
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen((v) => !v);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        setItemAppointment,
        updateItemDoctor,
        clearItemTime,
        clearCart,
        totalItems,
        totalPrice,
        isOpen,
        openCart,
        closeCart,
        toggleCart,
        isInCart,
        getItemAppointment,
        isSlotTaken,
        isTimeConflict,
        getOccupiedEndTime,
        getOccupiedIntervals,
        getEffectiveDuration,
        getChildren,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
